import { getCalendarEventFeedbackWindowState } from 'shared-helpers/calendar';

import type { CalendarEvent } from 'shared-types';
import type { GetCalendarEventUsersRes } from 'shared-types/responses';
import type { ReduxRootState } from 'types';

interface Params {
  calendarEvent: Pick<
    CalendarEvent,
    | 'endDate'
    | 'feedbackAccessEndDate'
    | 'feedbackAccessStartDate'
    | 'feedbackAllowNonParticipated'
  >;
  messages: ReduxRootState['messages'];
  now: number;
  user?: GetCalendarEventUsersRes['users'][0];
}

/**
 * Returns the reason why the feedback request email cannot be sent, to show as
 * a tooltip on the disabled send button, or undefined when it can be sent.
 * Without a `user` only the event level reasons are checked, which is what
 * sending to every participant needs. With one, the same per participant rules
 * the send route enforces are checked as well - including guest accounts, which
 * cannot give feedback at all.
 */
const getCalendarEventFeedbackEmailDisabledReason = ({
  calendarEvent,
  messages,
  now,
  user
}: Params) => {
  const windowState = getCalendarEventFeedbackWindowState({
    event: calendarEvent,
    now
  });

  if (windowState === 'not_open_yet') {
    return messages.eventCalendarFeedbackSendEmailNotOpenYet;
  }

  if (windowState === 'closed') {
    return messages.eventCalendarFeedbackSendEmailClosed;
  }

  if (!user) return undefined;

  if (user.feedbackSubmittedAt) {
    return messages.eventCalendarFeedbackSendEmailAlreadySubmitted;
  }

  if (user.roles?.includes('guest')) {
    return messages.eventCalendarFeedbackStatusNotAvailableGuestTooltip;
  }

  if (!calendarEvent.feedbackAllowNonParticipated && !user.participated) {
    return messages.eventCalendarFeedbackStatusNotAvailableTooltip;
  }

  if (!user.email) return messages.userNoEmail;

  if (
    user.locked ||
    (user.expirationDate && now > new Date(user.expirationDate).getTime())
  ) {
    return messages.emailUserAccountLockedOrExpired;
  }

  return undefined;
};

export default getCalendarEventFeedbackEmailDisabledReason;
